import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PortalToolAccess, { ClientServices } from '../components/PortalToolAccess';
import PortalLogins from '../components/PortalLogins';
import WindsorConnect from '../components/WindsorConnect';
import { GoogleBusinessConnect } from '../components/GoogleBusinessConnect';
import { AdsManagement } from '../components/AdsManagement';
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import ProviderMappings from "../components/ProviderMappings";
import { BrightLocalLinks } from "../components/BrightLocalSetup";
import { GhlSetup } from "../components/IntegrationSetup";
import ClientKeys, { type ClientProvider } from "../components/ClientKeys";
import SlackChannels from "../components/SlackChannels";

/** The sections, opened from the Setup tabs above the page (nav.ts) by `?view=`. */
const TITLES: Record<string, string> = { connections: "Connections", tools: "Portal", slack: "Slack", keys: "API keys" };

/** One client's connections, portal, Slack channels and its own copies of agency keys. Superadmin only. */
export default function ClientSettings() {
  const { user } = useAuth();
  const { clientId, client } = useClientSelection();
  const [params, setParams] = useSearchParams();
  // "tracking" is the old name of the Slack tab (tracking sites moved to the Tracking page).
  // Rename it in the URL so the Setup tabs above the page mark Slack too.
  const legacy = params.get("view") === "tracking";
  useEffect(() => {
    if (legacy) setParams((current) => { const next = new URLSearchParams(current); next.set("view", "slack"); return next; }, { replace: true });
  }, [legacy, setParams]);
  const asked = legacy ? "slack" : params.get("view") || "";
  const view = TITLES[asked] ? asked : "connections";
  const admin = user?.role === "superadmin";
  return (
    <>
      <PageHeader
        title={TITLES[view]}
        description={admin && clientId && view === "connections"
          ? `Accounts linked to ${client?.name || "this client"}. Agency-wide keys stay in Settings.`
          : undefined}
      />
      {!admin
        ? <EmptyState title="Superadmins only" description="Only a superadmin can change a client's connections, portal, Slack channels and API keys." />
        : clientId ? <Body key={clientId} clientId={clientId} clientName={client?.name || "this client"} clientStatus={client?.status} view={view} /> : <NoClient />}
    </>
  );
}

function Body({ clientId, clientName, clientStatus, view }: { clientId: number; clientName: string; clientStatus?: string; view: string }) {
  const [params] = useSearchParams();
  const windsor = useApi<{ configured: boolean }>(view === "connections" ? "/api/windsor/links?client_id=" + clientId : null);
  const keys = useApi<{ vault_ready: boolean; providers: ClientProvider[] }>(view === "connections" || view === "keys" ? `/api/client-settings/${clientId}/credentials` : null);
  const vaultReady = Boolean(keys.data?.vault_ready);
  const blSource = keys.data?.providers.find((p) => p.id === "brightlocal")?.source;
  const brightLocal = <section className="panel workflow-panel" aria-label="BrightLocal connections">
    {keys.error ? <ErrorState message={keys.error} onRetry={keys.reload} />
      : !keys.data ? <LoadingState label="Loading BrightLocal locations" />
      : blSource === "none" ? <>
          <h3>BrightLocal locations</h3>
          <p className="client-settings-hint">No BrightLocal key yet. Add one for this client, or the agency key in Settings, to link its locations.</p>
          <div className="client-settings-actions">
            <Link className="button secondary" to={`/client-settings?client_id=${clientId}&view=keys`}>Add client key</Link>
            <Link className="button secondary" to="/settings#integrations">Set up in Settings</Link>
          </div>
        </>
      : <BrightLocalLinks key={blSource} clientId={clientId} onChanged={() => {}} />}
  </section>;
  const brightLocalFirst = params.get("section") === "brightlocal";
  return (
    <div className="settings-stack client-settings">
      {view === 'tools' && <><ClientServices clientId={clientId}/><PortalToolAccess clientId={clientId}/><PortalLogins clientId={clientId} clientName={clientName} /></>}
      {view === 'connections' && <>
      {brightLocalFirst && brightLocal}
      <AdsManagement key={`management-${clientId}`} clientId={clientId} />
      <GoogleBusinessConnect key={clientId} clientId={clientId} />
      <section className="panel workflow-panel" aria-label="Windsor connections">
        {windsor.error ? <ErrorState message={windsor.error} onRetry={windsor.reload} /> : windsor.loading ? <LoadingState label="Loading Windsor connection" /> : <WindsorConnect configured={Boolean(windsor.data?.configured)} clientId={clientId} />}
      </section>
      <section className="panel workflow-panel">
        <GhlSetup clientId={clientId} vaultReady={vaultReady} onChanged={() => {}} />
      </section>
      <ProviderMappings />
      {!brightLocalFirst && brightLocal}
      </>}
      {view === 'slack' && <SlackChannels clientId={clientId} churned={clientStatus === 'churned'} />}
      {view === 'keys' && <>
      <p className="workflow-note">
        When a client has its own key, work for that client uses it. Everything else keeps using the agency key from Settings.
      </p>
      {keys.error ? <ErrorState message={keys.error} onRetry={keys.reload} />
        : !keys.data ? <LoadingState label="Loading client keys" />
        : <ClientKeys clientId={clientId} providers={keys.data.providers} vaultReady={vaultReady} onChanged={keys.reload} />}
      </>}
    </div>
  );
}
